import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, BehaviorSubject } from 'rxjs';
import { 
  Domain, 
  Enterprise, 
  Event, 
  Institution, 
  Pharmacy, 
  Advertisement, 
  Sector, 
  SubSector,
  Announcement 
} from '../models/business.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})

export class DataService {
  private apiUrl = environment.apiUrl;
  private imageBaseUrl = 'https://annuaireduniger.com/annuaire/public/uploads';
  private currentPage = new BehaviorSubject<number>(1);
  private defaultLogo = 'assets/img/img-placeholder.png'; // Logo par défaut de l'application

  constructor(private http: HttpClient) { }

  // Fonction utilitaire pour trier par libellé/nom
  private sortByName<T extends { libelle?: string; nom?: string }>(items: T[]): T[] {
    return items.sort((a, b) => {
      const nameA = (a.libelle || a.nom || '').toLowerCase();
      const nameB = (b.libelle || b.nom || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  // Domaines
  getDomains(): Observable<Domain[]> {
    return this.http.get<Domain[]>(`${this.apiUrl}/domaines`).pipe(
      map(domains => this.sortByName(domains)),
      map(domains => domains.map(domain => ({
        ...domain,
        path: domain.path ? `${this.imageBaseUrl}/ImagesDomaine/${domain.path}` : this.defaultLogo
      })))
    );
  }

  getDomainById(id: number): Observable<Domain> {
    return this.http.get<Domain>(`${this.apiUrl}/domaines/${id}`);
  }

  // Entreprises
  getEnterprises(params?: any): Observable<Enterprise[]> {
    return this.http.get<Enterprise[]>(`${this.apiUrl}/entreprises`).pipe(
      map(enterprises => this.sortByName(enterprises)),
      map(enterprises => enterprises.map(enterprise => ({
        ...enterprise,
        path: enterprise.path ? `${this.imageBaseUrl}/ImagesEntreprise/${enterprise.path}` : this.defaultLogo
      }))),
      map(enterprises => {
        return enterprises;
      })
    );
  }

  /*getEnterpriseById(id: number): Observable<Enterprise> {
    return this.http.get<Enterprise>(`${this.apiUrl}/entreprises/${id}`);
  }*/

  getEnterprisesBySubSector(subSectorId: number): Observable<Enterprise[]> {
    return this.http.get<Enterprise[]>(`${this.apiUrl}/entreprises/${subSectorId}`);
  }

  // Événements
  getEvents(): Observable<Event[]> {
    return this.http.get<Event[]>(`${this.apiUrl}/evenements`).pipe(
      map(events => this.sortByName(events)),
      map(events => events.map(event => ({
        ...event,
        path: event.path ? `${this.imageBaseUrl}/ImagesEvenement/${event.path}` : this.defaultLogo
      })))
    );
  }

  // Institutions
  getInstitutions(): Observable<Institution[]> {
    return this.http.get<Institution[]>(`${this.apiUrl}/institutions`).pipe(
      map(institutions => institutions.map(institution => ({
        ...institution,
        path: institution.path ? `${this.imageBaseUrl}/ImagesInstitution/${institution.path}` : null
      })))
    );
  }

  // Pharmacies
  getPharmacies(): Observable<Pharmacy[]> {
    return this.http.get<Pharmacy[]>(`${this.apiUrl}/pharmacies`);
  }

  // Publicités
  getAdvertisements(): Observable<Advertisement[]> {
    return this.http.get<Advertisement[]>(`${this.apiUrl}/publicites`).pipe(
      map(ads => this.sortByName(ads)),
      map(ads => ads.map(ad => ({
        ...ad,
        path: ad.path ? `${this.imageBaseUrl}/ImagesPublicite/${ad.path}` : this.defaultLogo
      })))
    );
  }

  // Secteurs
  getSectors(): Observable<Sector[]> {
    return this.http.get<Sector[]>(`${this.apiUrl}/secteurs`).pipe(
      map(sectors => sectors.map(sector => ({
        ...sector,
        path: sector.path ? `${this.imageBaseUrl}/ImagesSecteur/${sector.path}` : null
      })))
    );
  }

  getSectorsByDomain(domainId: number): Observable<Sector[]> {
    return this.http.get<Sector[]>(`${this.apiUrl}/secteurs/${domainId}`);
  }

  // Sous-secteurs
  getSubSectors(sectorId: number): Observable<SubSector[]> {
    return this.http.get<SubSector[]>(`${this.apiUrl}/sous_secteurs/${sectorId}`);
  }

  // Annonces
  getAnnouncements(): Observable<Announcement[]> {
    return this.http.get<Announcement[]>(`${this.apiUrl}/annonces`);
  }

  // Recherche
  search(query: string): Observable<Enterprise[]> {
    return this.http.get<Enterprise[]>(`${this.apiUrl}/search`, {
      params: { q: query }
    });
  }
} 